import { Fragment, type CSSProperties, type ReactNode } from "react";

/**
 * MarkdownText — a small, dependency-free renderer for the subset of Markdown the AI emits in
 * step bodies: paragraphs / line breaks, **bold** (rendered as real bold — no literal "**"),
 * bullet (`-`/`*`) and numbered (`1.`) lists, and GFM pipe tables.
 *
 * It also normalises a literal "\n" (backslash + n, which Gemini sometimes emits instead of a
 * real newline) into an actual line break, and highlights marked phrases inline:
 *   - `terms`         → official highlighter swipe (.hl)
 *   - `personalTerms` → the reader's personal marks (distinct coral underline)
 *
 * Kept intentionally lightweight per the project's "no heavy deps / no UI kit" rule.
 */

interface MarkdownTextProps {
  text: string;
  /** official marked phrases — wrapped in the brand highlighter swipe (.hl). */
  terms?: string[];
  /** the reader's personal marked phrases — rendered with a distinct coral underline. */
  personalTerms?: string[];
  className?: string;
  style?: CSSProperties;
}

const PERSONAL_COLOR = "#FF8FB1";

/** Turn escaped sequences (literal backslash-n / -t / -r) and CRLF into real whitespace. */
function normalize(raw: string): string {
  return raw
    .replace(/\\r\\n/g, "\n")
    .replace(/\\n/g, "\n")
    .replace(/\\t/g, "\t")
    .replace(/\\r/g, "\n")
    .replace(/\r\n/g, "\n")
    .replace(/\r/g, "\n");
}

type Block =
  | { kind: "p"; lines: string[] }
  | { kind: "ul"; items: string[] }
  | { kind: "ol"; items: string[] }
  | { kind: "table"; header: string[]; rows: string[][] };

const isUl = (l: string) => /^\s*[-*]\s+/.test(l);
const isOl = (l: string) => /^\s*\d+\.\s+/.test(l);
const isTableRow = (l: string) => l.trim().startsWith("|") && l.includes("|");
const isTableSep = (l: string) => /^\s*\|?[\s:|-]*-{1,}[\s:|-]*\|?\s*$/.test(l) && l.includes("-");

function parseRow(line: string): string[] {
  let s = line.trim();
  if (s.startsWith("|")) s = s.slice(1);
  if (s.endsWith("|")) s = s.slice(0, -1);
  return s.split("|").map((c) => c.trim());
}

function parseBlocks(text: string): Block[] {
  const lines = normalize(text).split("\n");
  const blocks: Block[] = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    if (line.trim() === "") {
      i += 1;
      continue;
    }
    // table: a header row immediately followed by a |---|---| separator
    if (isTableRow(line) && i + 1 < lines.length && isTableSep(lines[i + 1])) {
      const header = parseRow(line);
      i += 2; // skip header + separator
      const rows: string[][] = [];
      while (i < lines.length && isTableRow(lines[i])) {
        rows.push(parseRow(lines[i]));
        i += 1;
      }
      blocks.push({ kind: "table", header, rows });
      continue;
    }
    if (isUl(line)) {
      const items: string[] = [];
      while (i < lines.length && isUl(lines[i])) {
        items.push(lines[i].replace(/^\s*[-*]\s+/, ""));
        i += 1;
      }
      blocks.push({ kind: "ul", items });
      continue;
    }
    if (isOl(line)) {
      const items: string[] = [];
      while (i < lines.length && isOl(lines[i])) {
        items.push(lines[i].replace(/^\s*\d+\.\s+/, ""));
        i += 1;
      }
      blocks.push({ kind: "ol", items });
      continue;
    }
    const para: string[] = [];
    while (i < lines.length && lines[i].trim() !== "" && !isUl(lines[i]) && !isOl(lines[i]) && !isTableRow(lines[i])) {
      para.push(lines[i]);
      i += 1;
    }
    blocks.push({ kind: "p", lines: para });
  }
  return blocks;
}

// ---------- inline (bold + highlight) ----------

interface HlGroup {
  terms: string[];
  render: (children: ReactNode, key: number) => ReactNode;
}

interface Seg {
  start: number;
  end: number;
  gi: number;
}

/** Non-overlapping matches of all groups' terms, longer terms and earlier groups winning. */
function highlightSegments(text: string, groups: HlGroup[]): Seg[] {
  const all: { term: string; gi: number }[] = [];
  groups.forEach((g, gi) =>
    g.terms.forEach((t) => {
      const tt = t.trim();
      if (tt) all.push({ term: tt, gi });
    }),
  );
  // Longer first; on tie, earlier group (official) first.
  all.sort((a, b) => b.term.length - a.term.length || a.gi - b.gi);
  const taken = new Array(text.length).fill(-1);
  const segs: Seg[] = [];
  for (const { term, gi } of all) {
    let from = 0;
    while (from <= text.length - term.length) {
      const idx = text.indexOf(term, from);
      if (idx < 0) break;
      let free = true;
      for (let k = idx; k < idx + term.length; k++) {
        if (taken[k] !== -1) {
          free = false;
          break;
        }
      }
      if (free) {
        for (let k = idx; k < idx + term.length; k++) taken[k] = gi;
        segs.push({ start: idx, end: idx + term.length, gi });
      }
      from = idx + term.length;
    }
  }
  return segs.sort((a, b) => a.start - b.start);
}

function renderInlineChunk(text: string, groups: HlGroup[], keyBase: string): ReactNode {
  if (groups.every((g) => g.terms.length === 0)) return text;
  const segs = highlightSegments(text, groups);
  if (segs.length === 0) return text;
  const out: ReactNode[] = [];
  let cursor = 0;
  segs.forEach((s, i) => {
    if (s.start > cursor) out.push(<Fragment key={`${keyBase}t${i}`}>{text.slice(cursor, s.start)}</Fragment>);
    out.push(groups[s.gi].render(text.slice(s.start, s.end), i));
    cursor = s.end;
  });
  if (cursor < text.length) out.push(<Fragment key={`${keyBase}tail`}>{text.slice(cursor)}</Fragment>);
  return out;
}

/** Render one line of inline markdown: **bold** split, with highlight groups applied within each chunk. */
function Inline({ text, groups }: { text: string; groups: HlGroup[] }): ReactNode {
  const out: ReactNode[] = [];
  const re = /\*\*([^*]+)\*\*/g;
  let last = 0;
  let m: RegExpExecArray | null;
  let key = 0;
  while ((m = re.exec(text))) {
    if (m.index > last) out.push(<Fragment key={key++}>{renderInlineChunk(text.slice(last, m.index), groups, `n${key}`)}</Fragment>);
    out.push(<strong key={key++}>{renderInlineChunk(m[1], groups, `b${key}`)}</strong>);
    last = re.lastIndex;
  }
  if (last < text.length) out.push(<Fragment key={key++}>{renderInlineChunk(text.slice(last), groups, `e${key}`)}</Fragment>);
  return <>{out}</>;
}

export function MarkdownText({ text, terms = [], personalTerms = [], className, style }: MarkdownTextProps) {
  const groups: HlGroup[] = [
    { terms, render: (children, key) => <span key={`o${key}`} className="hl">{children}</span> },
    {
      terms: personalTerms,
      render: (children, key) => (
        <span key={`p${key}`} style={{ borderBottom: `3px solid ${PERSONAL_COLOR}`, paddingBottom: 1 }}>
          {children}
        </span>
      ),
    },
  ];
  const blocks = parseBlocks(text);

  return (
    <div className={className} style={style}>
      {blocks.map((b, bi) => {
        if (b.kind === "ul") {
          // overflow:hidden makes the list a block formatting context so its markers sit BESIDE a
          // floated image (e.g. Preview) instead of sliding under it. Custom markers give a clean,
          // clearly-indented "一段下げ" with the brand highlighter accent.
          return (
            <ul key={bi} style={listBlockStyle}>
              {b.items.map((it, ii) => (
                <li key={ii} style={listItemStyle}>
                  <span aria-hidden="true" style={ulMarkerWrap}>
                    <CheckMark />
                  </span>
                  <span style={listBodyStyle}><Inline text={it} groups={groups} /></span>
                </li>
              ))}
            </ul>
          );
        }
        if (b.kind === "ol") {
          return (
            <ol key={bi} style={listBlockStyle}>
              {b.items.map((it, ii) => (
                <li key={ii} style={listItemStyle}>
                  <span aria-hidden="true" style={olMarkerStyle}>{ii + 1}</span>
                  <span style={listBodyStyle}><Inline text={it} groups={groups} /></span>
                </li>
              ))}
            </ol>
          );
        }
        if (b.kind === "table") {
          // clear:both drops the table to full width BELOW a floated image (Preview) — no cramped
          // beside-float column and no horizontal scrollbar (cells wrap instead). className="tbl"
          // reuses the design-system table styling for visual consistency.
          return (
            <div key={bi} style={{ clear: "both", margin: "14px 0" }}>
              <table className="tbl">
                <thead>
                  <tr>
                    {b.header.map((h, hi) => (
                      <th key={hi}><Inline text={h} groups={groups} /></th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {b.rows.map((r, ri) => (
                    <tr key={ri}>
                      {r.map((cell, ci) => (
                        <td key={ci} className="jp"><Inline text={cell} groups={groups} /></td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        }
        // paragraph: lines joined by <br/>
        return (
          <p key={bi} className="jp" style={{ margin: bi < blocks.length - 1 ? "0 0 10px" : 0, lineHeight: 1.85 }}>
            {b.lines.map((ln, li) => (
              <Fragment key={li}>
                {li > 0 ? <br /> : null}
                <Inline text={ln} groups={groups} />
              </Fragment>
            ))}
          </p>
        );
      })}
    </div>
  );
}

// List rendering: custom markers (no browser list-style) so indent + marker style are fully
// controlled and floats never overlap the markers. overflow:hidden establishes a BFC.
const listBlockStyle: CSSProperties = {
  listStyle: "none",
  margin: "8px 0 14px",
  padding: 0,
  overflow: "hidden",
};
const listItemStyle: CSSProperties = {
  display: "flex",
  gap: 12,
  alignItems: "flex-start",
  lineHeight: 1.85,
  marginBottom: 5,
};
const listBodyStyle: CSSProperties = { flex: 1, minWidth: 0 };
// Stylish SVG check as the bullet marker (vector — never mojibakes like a ✓ glyph can, and
// reads clearly on both surfaces). The lime "highlighter" stroke ties it to the brand.
function CheckMark() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" style={{ display: "block" }} aria-hidden="true">
      <path d="M2.8 8.6l3.1 3.2L13.2 4.3" stroke="var(--ink)" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
const ulMarkerWrap: CSSProperties = {
  flex: "0 0 auto",
  marginTop: "0.32em",
  lineHeight: 0,
};
const olMarkerStyle: CSSProperties = {
  flex: "0 0 auto",
  minWidth: "1.5em",
  fontFamily: "var(--font-mono)",
  fontSize: 13,
  fontWeight: 700,
  lineHeight: 1.85,
  color: "var(--ink)",
  textAlign: "right",
};
